import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { APITester } from "@/APITester";
import { getErrorMessage, isApiError, requestJsonOrThrow } from "@/app/http";
import type { AdminUser, ApiKeyListItem, ServiceSummary, User } from "@/app/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

interface AdminPageProps {
  currentUser: User;
  onUnauthorized: () => void;
}

type AdminTab = "users" | "keys";

function parseIdFromText(value: string): number | null {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed;
}

export function AdminPage({ currentUser, onUnauthorized }: AdminPageProps) {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<AdminTab>("users");

  const [adminCreateUsername, setAdminCreateUsername] = useState("");
  const [adminCreatePassword, setAdminCreatePassword] = useState("");
  const [adminCreateIsAdmin, setAdminCreateIsAdmin] = useState(false);

  const [selectedApiUserId, setSelectedApiUserId] = useState<number | null>(null);
  const [newKeyName, setNewKeyName] = useState("");
  const [newKeyToken, setNewKeyToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const servicesQuery = useQuery({
    queryKey: ["admin", "services"],
    queryFn: async () => {
      const data = await requestJsonOrThrow<{ services: ServiceSummary[] }>("/api/admin/services");
      return data.services;
    },
  });

  const usersQuery = useQuery({
    queryKey: ["admin", "users"],
    queryFn: async () => {
      const data = await requestJsonOrThrow<{ users: AdminUser[] }>("/api/admin/users");
      return data.users;
    },
  });

  const apiKeysQuery = useQuery({
    queryKey: ["admin", "api-keys", selectedApiUserId],
    enabled: selectedApiUserId !== null,
    queryFn: async () => {
      const data = await requestJsonOrThrow<{ user: { id: number; username: string }; keys: ApiKeyListItem[] }>(
        `/api/admin/users/${selectedApiUserId}/api-keys`,
      );
      return data;
    },
  });

  const createUserMutation = useMutation({
    mutationFn: async () => {
      return requestJsonOrThrow<{ ok: boolean }>("/api/admin/users", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: adminCreateUsername,
          password: adminCreatePassword,
          isAdmin: adminCreateIsAdmin,
        }),
      });
    },
    onSuccess() {
      setAdminCreateUsername("");
      setAdminCreatePassword("");
      setAdminCreateIsAdmin(false);
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
    onError(error) {
      if (isApiError(error) && error.status === 401) {
        onUnauthorized();
      }
    },
  });

  const toggleAdminMutation = useMutation({
    mutationFn: async ({ targetUserId, isAdmin }: { targetUserId: number; isAdmin: boolean }) => {
      return requestJsonOrThrow<{ ok: boolean }>(`/api/admin/users/${targetUserId}/admin`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isAdmin }),
      });
    },
    onSuccess() {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
    onError(error) {
      if (isApiError(error) && error.status === 401) {
        onUnauthorized();
      }
    },
  });

  const toggleServiceAccessMutation = useMutation({
    mutationFn: async ({ targetUserId, serviceKey, isEnabled }: { targetUserId: number; serviceKey: string; isEnabled: boolean }) => {
      return requestJsonOrThrow<{ ok: boolean }>(`/api/admin/users/${targetUserId}/services/${serviceKey}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isEnabled }),
      });
    },
    onSuccess() {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
    onError(error) {
      if (isApiError(error) && error.status === 401) {
        onUnauthorized();
      }
    },
  });

  const createApiKeyMutation = useMutation({
    mutationFn: async ({ userId, name }: { userId: number; name: string }) => {
      return requestJsonOrThrow<{ key: ApiKeyListItem; token: string }>(`/api/admin/users/${userId}/api-keys`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      });
    },
    onSuccess(data) {
      setNewKeyToken(data.token);
      setNewKeyName("");
      setCopied(false);
      void queryClient.invalidateQueries({ queryKey: ["admin", "api-keys", selectedApiUserId] });
    },
    onError(error) {
      if (isApiError(error) && error.status === 401) {
        onUnauthorized();
      }
    },
  });

  const revokeApiKeyMutation = useMutation({
    mutationFn: async ({ userId, keyId }: { userId: number; keyId: number }) => {
      return requestJsonOrThrow<{ ok: boolean }>(`/api/admin/users/${userId}/api-keys/${keyId}`, { method: "DELETE" });
    },
    onSuccess() {
      void queryClient.invalidateQueries({ queryKey: ["admin", "api-keys", selectedApiUserId] });
    },
    onError(error) {
      if (isApiError(error) && error.status === 401) {
        onUnauthorized();
      }
    },
  });

  useEffect(() => {
    const users = usersQuery.data ?? [];
    if (users.length === 0) {
      setSelectedApiUserId(null);
      return;
    }

    if (selectedApiUserId === null || !users.some(user => user.id === selectedApiUserId)) {
      setSelectedApiUserId(users[0]?.id ?? null);
      setNewKeyToken(null);
      setCopied(false);
    }
  }, [selectedApiUserId, usersQuery.data]);

  useEffect(() => {
    if (
      (isApiError(servicesQuery.error) && servicesQuery.error.status === 401) ||
      (isApiError(usersQuery.error) && usersQuery.error.status === 401) ||
      (isApiError(apiKeysQuery.error) && apiKeysQuery.error.status === 401)
    ) {
      onUnauthorized();
    }
  }, [apiKeysQuery.error, onUnauthorized, servicesQuery.error, usersQuery.error]);

  const adminServices = servicesQuery.data ?? [];
  const adminUsers = usersQuery.data ?? [];
  const selectedApiUser = adminUsers.find(user => user.id === selectedApiUserId) ?? null;
  const apiKeys = apiKeysQuery.data?.keys ?? [];

  const usersLoading = servicesQuery.isPending || usersQuery.isPending;
  const apiKeysLoading = usersQuery.isPending || (selectedApiUserId !== null && apiKeysQuery.isPending);

  const adminError = useMemo(() => {
    const errors = [
      servicesQuery.error,
      usersQuery.error,
      apiKeysQuery.error,
      createUserMutation.error,
      toggleAdminMutation.error,
      toggleServiceAccessMutation.error,
      createApiKeyMutation.error,
      revokeApiKeyMutation.error,
    ];

    for (const error of errors) {
      if (error && !(isApiError(error) && error.status === 401)) {
        return getErrorMessage(error, "Request failed");
      }
    }

    return null;
  }, [
    apiKeysQuery.error,
    createApiKeyMutation.error,
    createUserMutation.error,
    revokeApiKeyMutation.error,
    servicesQuery.error,
    toggleAdminMutation.error,
    toggleServiceAccessMutation.error,
    usersQuery.error,
  ]);

  const isDefaultAdminSelf = (adminUser: AdminUser): boolean =>
    currentUser.username === "bun-server" && currentUser.id === adminUser.id && adminUser.isAdmin;

  const handleCreateAdminUser = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      await createUserMutation.mutateAsync();
    } catch {
      // Mutation error is surfaced via query state.
    }
  };

  const handleToggleAdmin = async (targetUserId: number, isAdmin: boolean) => {
    try {
      await toggleAdminMutation.mutateAsync({ targetUserId, isAdmin });
    } catch {
      // Mutation error is surfaced via query state.
    }
  };

  const handleToggleServiceAccess = async (targetUserId: number, serviceKey: string, isEnabled: boolean) => {
    try {
      await toggleServiceAccessMutation.mutateAsync({ targetUserId, serviceKey, isEnabled });
    } catch {
      // Mutation error is surfaced via query state.
    }
  };

  const handleCreateApiKey = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (selectedApiUserId === null) return;

    const name = newKeyName.trim();
    if (!name) return;

    try {
      await createApiKeyMutation.mutateAsync({ userId: selectedApiUserId, name });
    } catch {
      // Mutation error is surfaced via query state.
    }
  };

  const handleRevokeApiKey = async (keyId: number) => {
    if (selectedApiUserId === null) return;

    try {
      await revokeApiKeyMutation.mutateAsync({ userId: selectedApiUserId, keyId });
    } catch {
      // Mutation error is surfaced via query state.
    }
  };

  const copyToken = async () => {
    if (!newKeyToken) return;
    try {
      await navigator.clipboard.writeText(newKeyToken);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="grid gap-4">
      <Card>
        <CardHeader>
          <CardTitle>Platform Admin</CardTitle>
          <CardDescription>Manage users, service access, and API keys.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Button type="button" variant={activeTab === "users" ? "default" : "outline"} onClick={() => setActiveTab("users")}>
            Users
          </Button>
          <Button type="button" variant={activeTab === "keys" ? "default" : "outline"} onClick={() => setActiveTab("keys")}>
            API Keys
          </Button>
        </CardContent>
      </Card>

      {adminError ? <p className="text-sm text-red-600">{adminError}</p> : null}

      {activeTab === "users" ? (
        <Card>
          <CardHeader>
            <CardTitle>Users & Service Access</CardTitle>
            <CardDescription>Create users, grant admin role, and toggle service-level access.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4">
            <form onSubmit={handleCreateAdminUser} className="grid gap-3 rounded-lg border p-4">
              <p className="text-sm font-medium">Create User</p>
              <div className="grid gap-2 md:grid-cols-2">
                <Input
                  value={adminCreateUsername}
                  onChange={event => setAdminCreateUsername(event.currentTarget.value)}
                  placeholder="new-user"
                />
                <Input
                  type="password"
                  value={adminCreatePassword}
                  onChange={event => setAdminCreatePassword(event.currentTarget.value)}
                  placeholder="Temporary password"
                />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={adminCreateIsAdmin}
                  onChange={event => setAdminCreateIsAdmin(event.currentTarget.checked)}
                />
                Create as admin
              </label>
              <Button type="submit" disabled={createUserMutation.isPending}>
                {createUserMutation.isPending ? "Creating..." : "Create user"}
              </Button>
            </form>

            {usersLoading ? (
              <p className="text-sm text-muted-foreground">Loading admin data...</p>
            ) : (
              <div className="grid gap-3">
                {adminUsers.map(adminUser => (
                  <div key={adminUser.id} className="grid gap-3 rounded-lg border p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <p className="font-medium">{adminUser.username}</p>
                        <p className="text-xs text-muted-foreground">
                          Last login: {adminUser.lastLoginAt ? new Date(adminUser.lastLoginAt).toLocaleString() : "Never"}
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant={adminUser.isAdmin ? "default" : "outline"}
                        disabled={isDefaultAdminSelf(adminUser)}
                        onClick={() => void handleToggleAdmin(adminUser.id, !adminUser.isAdmin)}
                      >
                        {isDefaultAdminSelf(adminUser)
                          ? "Admin (Locked)"
                          : adminUser.isAdmin
                            ? "Admin"
                            : "Standard"}
                      </Button>
                    </div>

                    <div className="grid gap-2">
                      <p className="text-sm font-medium">Service Access</p>
                      {adminServices.length === 0 ? (
                        <p className="text-xs text-muted-foreground">No registered services.</p>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {adminServices.map(service => {
                            const enabled = Boolean(adminUser.serviceAccess[service.serviceKey]);
                            return (
                              <Button
                                key={`${adminUser.id}-${service.serviceKey}`}
                                type="button"
                                variant={enabled ? "default" : "outline"}
                                onClick={() => void handleToggleServiceAccess(adminUser.id, service.serviceKey, !enabled)}
                                title={service.description}
                              >
                                {service.displayName}: {enabled ? "On" : "Off"}
                              </Button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          <Card>
            <CardHeader>
              <CardTitle>Manage API Keys</CardTitle>
              <CardDescription>Create and revoke API keys per user.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4">
              <div className="grid gap-2">
                <label htmlFor="admin-api-key-user" className="text-sm font-medium">
                  Target user
                </label>
                <select
                  id="admin-api-key-user"
                  className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                  value={selectedApiUserId ?? ""}
                  onChange={event => {
                    const nextUserId = parseIdFromText(event.currentTarget.value);
                    setSelectedApiUserId(nextUserId);
                    setNewKeyToken(null);
                    setCopied(false);
                  }}
                >
                  {adminUsers.length === 0 ? <option value="">No users</option> : null}
                  {adminUsers.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.username} {user.isAdmin ? "(admin)" : ""}
                    </option>
                  ))}
                </select>
              </div>

              <form onSubmit={handleCreateApiKey} className="grid gap-3 md:grid-cols-[1fr_auto]">
                <Input
                  value={newKeyName}
                  onChange={event => setNewKeyName(event.currentTarget.value)}
                  placeholder="Key name"
                  disabled={selectedApiUserId === null}
                />
                <Button type="submit" disabled={selectedApiUserId === null || createApiKeyMutation.isPending}>
                  {createApiKeyMutation.isPending ? "Creating..." : "Create API key"}
                </Button>
              </form>

              {newKeyToken ? (
                <div className="grid gap-2 rounded-lg border p-4">
                  <p className="text-sm font-medium">New API Key (copy now)</p>
                  <code className="block overflow-x-auto rounded bg-muted p-2 text-xs">{newKeyToken}</code>
                  <div className="flex items-center gap-2">
                    <Button type="button" variant="secondary" onClick={() => void copyToken()}>
                      Copy
                    </Button>
                    {copied ? <span className="text-sm text-green-700">Copied</span> : null}
                  </div>
                </div>
              ) : null}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>API Key List</CardTitle>
              <CardDescription>
                {selectedApiUser ? `Showing keys for ${selectedApiUser.username}.` : "Select a user to manage keys."}
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-3">
              {apiKeysLoading ? (
                <p className="text-sm text-muted-foreground">Loading keys...</p>
              ) : selectedApiUserId === null ? (
                <p className="text-sm text-muted-foreground">No users available.</p>
              ) : apiKeys.length === 0 ? (
                <p className="text-sm text-muted-foreground">No API keys for this user.</p>
              ) : (
                apiKeys.map(key => (
                  <div key={key.id} className="grid gap-2 rounded-lg border p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <p className="font-medium">{key.name}</p>
                        <p className="font-mono text-xs text-muted-foreground">keyId: {key.keyId}</p>
                      </div>
                      {key.revokedAt ? (
                        <span className="text-sm text-muted-foreground">Revoked</span>
                      ) : (
                        <Button type="button" variant="outline" onClick={() => void handleRevokeApiKey(key.id)}>
                          Revoke
                        </Button>
                      )}
                    </div>
                    <div className="grid gap-1 text-xs text-muted-foreground">
                      <p>Created: {new Date(key.createdAt).toLocaleString()}</p>
                      <p>Last used: {key.lastUsedAt ? new Date(key.lastUsedAt).toLocaleString() : "Never"}</p>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>API Tester</CardTitle>
              <CardDescription>Use generated tokens to test service APIs.</CardDescription>
            </CardHeader>
            <CardContent>
              <APITester defaultApiKey={newKeyToken} />
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
